import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect} from 'react';
import isEqual from 'react-fast-compare';

import {pluralize} from 'BenchlingUtil';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ContainerSummary} from 'constants/SampleConstants';
import * as InventoryUnitUtil from 'inventory/InventoryUnitUtil';
import {getRestrictedSampleUsersDisplayText} from 'inventory/sample-status/RestrictedSampleUsersTextUtil';
import {SampleControlsFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevel';
import {hasMinimumSCRFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevelUtil';
import {
  canProceedWithSamples,
  checkSamplesForStatusWarnings,
  SampleStatusWarning,
} from 'inventory/sample-status/SampleStatusWarning';
import {BaseSampleStatusWarningTrackingParams} from 'inventory/sample-status/SampleStatusWarningTracking';
import NameTemplateInfoBanner from 'name-templates/NameTemplateInfoBanner';
import {getContainerLabelWithQuantity} from 'samples/SampleUtil';
import {PLATE_SCHEMA_TYPE} from 'samples/storables/StorableSchemaConstants';
import {TYPE as InputSampleType} from 'samples/transfers/input-sample/InputSampleConstants';
import renderPossibleInputContainerContentsError from 'samples/transfers/InputContainerContentsError';
import {
  ChooseTransferOutputPaneState,
  getConfirmedOutputContainers,
  getInputTagSchemaId,
  UpdateTransferModalStateHandler,
  UpdateTransferPaneSampleStatusStateHandler,
} from 'samples/transfers/TransferModalUtil';
import * as TransferUtil from 'samples/transfers/TransferUtil';
import {willTransferApplyContainerNameTemplate} from 'samples/transfers/TransferUtil';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import useOnMount from 'ui-library/hooks/useOnMount';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {GenericEvent} from 'util/Types';

import ChooseOutputContainers from './ChooseOutputContainers';
import ChooseOutputPlate from './ChooseOutputPlate';

export interface ChooseOutputPaneProps {
  paneState: ChooseTransferOutputPaneState;
  registryId?: string;
  sampleStatusWarningParams: BaseSampleStatusWarningTrackingParams & {
    shouldShowInputWarnings: boolean;
  };
  onUpdateSampleStatusState: UpdateTransferPaneSampleStatusStateHandler;
  onUpdateModalState: UpdateTransferModalStateHandler;
}

export function getIncompatibleUnitsOutputContainers({
  outputType,
  selectedInputContainer,
  outputContainerSummaries,
  outputCells,
}: ChooseTransferOutputPaneState): Array<ContainerSummary> {
  const selectedOutputContainers =
    outputType === ModelTypeConstants.CONTAINER
      ? outputContainerSummaries
      : outputCells.map((cell) => cell.container);

  if (selectedInputContainer == null) {
    return [];
  }
  return selectedOutputContainers.filter(
    (selectedOutputContainer) =>
      !InventoryUnitUtil.areUnitsCompatibleFromSymbols([
        selectedInputContainer.quantity.units,
        selectedOutputContainer.quantity.units,
      ])
  );
}

function willImplicitlyModifyDestinationSampleRoles({
  outputType,
  selectedInputContainer,
  outputContainerSummaries,
  outputCells,
  outputPlate,
}: ChooseTransferOutputPaneState): boolean {
  if (
    !hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V1) ||
    hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V3)
  ) {
    return false;
  }

  if (
    outputType === ModelTypeConstants.PLATE &&
    (outputPlate == null || outputPlate?.storableSchema.plateSchemaType === PLATE_SCHEMA_TYPE.FIXED_PLATE)
  ) {
    return false;
  }

  const outputContainers =
    outputType === ModelTypeConstants.CONTAINER
      ? outputContainerSummaries
      : outputCells.map((cell) => cell.container);

  return TransferUtil.willTransferModifyOutputSampleRoles(selectedInputContainer, outputContainers);
}

export function ChooseOutputValidationError({
  selectedInputContainer,
  incompatibleUnitsOutputContainers,
}: {
  selectedInputContainer: ContainerSummary;
  incompatibleUnitsOutputContainers: Array<ContainerSummary>;
}): JSX.Element | null {
  return (
    <div
      className="legacy-font-proxima-13"
      {...getTestProps({component: 'ChooseOutputValidationError', element: 'root'})}
    >
      <p className="u-danger-color">
        {`Cannot transfer from ${getContainerLabelWithQuantity(
          selectedInputContainer
        )} into the selected ${pluralize(
          incompatibleUnitsOutputContainers.length,
          'container'
        )} because their units are incompatible: ${incompatibleUnitsOutputContainers
          .map((container) => getContainerLabelWithQuantity(container))
          .join(', ')}`}
      </p>
    </div>
  );
}

function ChooseOutputPane({
  paneState,
  registryId,
  sampleStatusWarningParams,
  onUpdateSampleStatusState,
  onUpdateModalState,
}: ChooseOutputPaneProps): JSX.Element {
  const shouldShowErrorsForInputContainer =
    paneState.selectedInputContainer != null && sampleStatusWarningParams.shouldShowInputWarnings;
  useOnMount(() => {
    function checkForInputWarnings(): void {
      const {shouldShowWarning, samples} = checkSamplesForStatusWarnings({
        samples: [paneState.selectedInputContainer!],
      });
      const newInputStatusState = {
        ...paneState.inputSampleStatusState,
        shouldShowStatusWarnings: shouldShowWarning,
        samples: samples ?? null,
        selectedSampleIds: samples?.map(({id}) => id) ?? [],
      };
      if (!isEqual(newInputStatusState, paneState.inputSampleStatusState)) {
        onUpdateSampleStatusState(newInputStatusState, 'inputSampleStatusState');
      }
    }

    if (shouldShowErrorsForInputContainer) {
      checkForInputWarnings();
    }
  });

  useEffect(() => {
    willTransferApplyContainerNameTemplate(
      getInputTagSchemaId(
        paneState.createAndRegisterBioentityArgs,
        paneState.inputBioentity,
        paneState.inputType
      ),
      paneState.inputType === InputSampleType.EXISTING_CONTAINER
        ? (paneState.inputContainer?.id ?? null)
        : null,
      getConfirmedOutputContainers(
        paneState.outputCells,
        paneState.outputContainerSummaries,
        paneState.confirmedOutputContainerIds,
        paneState.outputType
      )
    ).then((willApplyNameTemplate) => {
      onUpdateModalState({showNameTemplateBanner: willApplyNameTemplate});
    });
  }, [
    paneState.createAndRegisterBioentityArgs,
    paneState.inputBioentity,
    paneState.inputContainer,
    paneState.inputType,
    paneState.outputCells,
    paneState.outputContainerSummaries,
    paneState.confirmedOutputContainerIds,
    paneState.outputType,
    onUpdateModalState,
  ]);

  const setSelectedSampleIds = (sampleIds: Array<string>): void => {
    onUpdateSampleStatusState({selectedSampleIds: sampleIds}, 'inputSampleStatusState');
  };

  const setCanProceedOnSampleStatus = (canProceed: boolean): void => {
    onUpdateSampleStatusState({canProceedOnSampleStatus: canProceed}, 'inputSampleStatusState');
  };

  const possibleWarning = shouldShowErrorsForInputContainer &&
    paneState.inputSampleStatusState.shouldShowStatusWarnings &&
    paneState.inputSampleStatusState.samples != null && (
      <div
        className="u-small-spacing-top"
        {...getTestProps({component: 'ChooseOutputPane', element: 'sampleStatusWarning'})}
      >
        <SampleStatusWarning
          sampleIds={[paneState.selectedInputContainer!.id]}
          selectedSampleIds={paneState.inputSampleStatusState.selectedSampleIds}
          setSelectedSampleIds={setSelectedSampleIds}
          shouldProceed={paneState.inputSampleStatusState.canProceedOnSampleStatus}
          setShouldProceed={setCanProceedOnSampleStatus}
          trackingParams={sampleStatusWarningParams}
          samples={paneState.inputSampleStatusState.samples}
        />
      </div>
    );

  const _handleChangeOutputType = (e: GenericEvent): void => {
    onUpdateModalState({
      outputType: e.target.value as ModelTypeConstants.CONTAINER | ModelTypeConstants.PLATE,
    });
  };

  const {selectedInputContainer} = paneState;
  const incompatibleUnitsOutputContainers = getIncompatibleUnitsOutputContainers(paneState);

  const outputPaneContent = (
    <div>
      <div {...getTestProps({component: 'ChooseOutputPane', element: 'destinationRadioGroup'})}>
        <FieldLabel label="Destination" />
        <div>
          <input
            aria-labelledby="destinationRadioGroup-container"
            checked={paneState.outputType === ModelTypeConstants.CONTAINER}
            onChange={_handleChangeOutputType}
            type="radio"
            value={ModelTypeConstants.CONTAINER}
          />
          <span id="destinationRadioGroup-container" className="u-tiny-spacing-left u-spacing-right">
            Container
          </span>
          <input
            aria-labelledby="destinationRadioGroup-plate"
            checked={paneState.outputType === ModelTypeConstants.PLATE}
            onChange={_handleChangeOutputType}
            type="radio"
            value={ModelTypeConstants.PLATE}
          />
          <span id="destinationRadioGroup-plate" className="u-tiny-spacing-left">
            Plate / Box
          </span>
        </div>
      </div>
      <div className="u-small-spacing-top">
        {paneState.outputType === ModelTypeConstants.CONTAINER ? (
          <ChooseOutputContainers
            containers={paneState.outputContainerSummaries}
            errors={paneState.errors}
            registryId={registryId}
            sampleStatusWarningTrackingParams={sampleStatusWarningParams}
            outputSampleStatusState={paneState.outputSampleStatusState}
            onUpdateSampleStatusState={onUpdateSampleStatusState}
            onUpdateModalState={onUpdateModalState}
          />
        ) : (
          <ChooseOutputPlate
            errors={paneState.errors}
            plate={paneState.outputPlate}
            registryId={registryId}
            selectedCells={paneState.outputCells}
            sampleStatusWarningTrackingParams={sampleStatusWarningParams}
            outputSampleStatusState={paneState.outputSampleStatusState}
            onUpdateSampleStatusState={onUpdateSampleStatusState}
            onUpdateModalState={onUpdateModalState}
          />
        )}
      </div>

      {selectedInputContainer != null && incompatibleUnitsOutputContainers.length > 0 && (
        <ChooseOutputValidationError
          selectedInputContainer={selectedInputContainer}
          incompatibleUnitsOutputContainers={incompatibleUnitsOutputContainers}
        />
      )}
      {paneState.showNameTemplateBanner && (
        <div className={css(styles.infoBanner)}>
          <NameTemplateInfoBanner
            dataTestAttrs={{component: 'ChooseOutputPane', element: 'nameTemplateInfoBanner'}}
            itemDescription="container"
            isPlural={
              getConfirmedOutputContainers(
                paneState.outputCells,
                paneState.outputContainerSummaries,
                paneState.confirmedOutputContainerIds,
                paneState.outputType
              ).length > 1
            }
          />
        </div>
      )}
    </div>
  );

  const possibleDestinationWarning = willImplicitlyModifyDestinationSampleRoles(paneState) && (
    <div className="u-small-spacing-top">
      <InlineBanner
        bannerType={InlineBannerType.INFO}
        dataTestAttrs={{component: 'ChooseOutputPane', element: 'destinationSampleRolesInfoBanner'}}
        text={`Any sample owners and ${getRestrictedSampleUsersDisplayText()} of the source container will be added to those of the destination containers.`}
        key="sample-control"
      />
    </div>
  );

  return (
    <div {...getTestProps({component: 'ChooseOutputPane', element: 'root'})}>
      {shouldShowErrorsForInputContainer &&
        renderPossibleInputContainerContentsError({inputContainer: paneState.selectedInputContainer!})}
      {possibleWarning}
      {canProceedWithSamples(
        paneState.inputSampleStatusState.selectedSampleIds,
        sampleStatusWarningParams.shouldShowInputWarnings &&
          paneState.inputSampleStatusState.shouldShowStatusWarnings,
        paneState.inputSampleStatusState.canProceedOnSampleStatus
      ) && outputPaneContent}
      {possibleDestinationWarning}
    </div>
  );
}

const styles = StyleSheet.create({
  infoBanner: {
    marginTop: 15,
  },
});

export default ChooseOutputPane;
